/**
 * Suggestions -- T&B-1 PR 5 (spec, "Screens"): draft entries proposed from
 * your own Practice events on billing jobs linked to an obligation type
 * ("you moved VAT201 Sep 2026 to For review on 12 Oct"). Accept with the
 * hours (and the job, where several are linked) or dismiss; nothing is ever
 * posted on its own. An accepted suggestion becomes a DRAFT on My week, to
 * submit with the rest of the week.
 *
 * The list itself (`SuggestionList`) is shared with My week, which shows the
 * week it is on.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type AuthState, type BillingJob, type ObligationTypeInUse, type Suggestion } from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { clientLabel, inputCls, labelCls, primaryBtn, quietBtn, todaySast, useBillingJobs } from '../components/TimeCapture'

const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })

const EVENT_WORDS = { for_review: 'Moved to For review', completed: 'Completed', note: 'Note' } as const

function whatHappened(s: Suggestion): string {
  const parts: string[] = s.events.filter((e) => e !== 'note').map((e) => EVENT_WORDS[e])
  if (s.notes > 0) parts.push(s.notes === 1 ? 'wrote a note' : `wrote ${s.notes} notes`)
  return parts.join(' · ')
}

/** Open suggestions with their days in [from, to]; `onChanged` after each accept or dismiss. */
export function SuggestionList({
  from,
  to,
  onChanged,
  empty,
  hideWhenEmpty = false,
  heading,
  reloadToken,
}: {
  from?: string
  to?: string
  onChanged?: () => void
  empty?: string
  /** Render nothing at all while there is nothing to show (My week). */
  hideWhenEmpty?: boolean
  /** When given, the list draws its own section with this heading. */
  heading?: string
  /** Changes whenever the caller's own data changed (My week's entries): the list reloads. */
  reloadToken?: string
}) {
  const [items, setItems] = useState<Suggestion[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const { jobs } = useBillingJobs()

  const load = useCallback(async () => {
    try {
      setItems((await api.suggestions(from, to)).suggestions)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    // reloadToken is a dependency on purpose: an entry added or deleted on My week can hide or reveal a suggestion.
  }, [from, to, reloadToken])
  useEffect(() => {
    void load()
  }, [load])

  const done = async (message: string) => {
    setNotice(message)
    await load()
    onChanged?.()
  }

  if (error)
    return hideWhenEmpty ? (
      <p className="mb-4 mt-0 text-[12.5px] text-eq-warning">Suggestions could not be loaded: {error}</p>
    ) : (
      <ErrorState message={error} />
    )
  if (items === null) return hideWhenEmpty ? null : <LoadingState what="suggestions" />
  if (hideWhenEmpty && items.length === 0 && notice === null) return null
  return (
    <div className={heading === undefined ? '' : 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'}>
      {heading !== undefined && <h2 className="mb-2 mt-0 font-heading text-[15px] font-bold text-eq-navy">{heading}</h2>}
      {notice && <p className="mb-2 mt-0 text-[13px] text-eq-success">{notice}</p>}
      {items.length === 0 ? (
        <p className="m-0 text-[13.5px] text-eq-neutral">{empty ?? 'No open suggestions.'}</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {items.map((s) => (
            <SuggestionRow key={s.key} s={s} jobs={jobs} onDone={done} />
          ))}
        </ul>
      )}
    </div>
  )
}

function SuggestionRow({ s, jobs, onDone }: { s: Suggestion; jobs: BillingJob[] | null; onDone: (message: string) => Promise<void> }) {
  const [hours, setHours] = useState('')
  const [jobId, setJobId] = useState(s.jobs.length === 1 ? s.jobs[0]!.id : '')
  const [taskId, setTaskId] = useState<string | null>(null)
  const [narrative, setNarrative] = useState(s.narrative)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tasks = (jobs?.find((j) => j.id === jobId)?.tasks ?? []).filter((t) => !t.archived)

  const act = async (what: 'accept' | 'dismiss') => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (what === 'accept') {
        const e = await api.acceptSuggestion({ key: s.key, hours, jobId: jobId || undefined, taskId, narrative })
        await onDone(
          `Logged ${e.hours} h on ${e.job.name} for ${clientLabel(s.client)} on ${dayLabel(s.date)} as a draft on My week. Submit that week — again, if it was already submitted — to send it for approval.`
        )
      } else {
        await api.dismissSuggestion(s.key)
        await onDone(`Dismissed: ${s.obligationType.code} ${s.period.label} for ${clientLabel(s.client)} on ${dayLabel(s.date)}.`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="border-t border-eq-border py-3 first:border-t-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[13.5px]">
          <span className="font-semibold">{dayLabel(s.date)}</span> · {clientLabel(s.client)} ·{' '}
          <span title={s.obligationType.description}>
            {s.obligationType.code} {s.period.label}
          </span>
        </div>
        <div className="text-[12.5px] text-eq-helper">{whatHappened(s)}</div>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className={labelCls}>
          Hours
          <input className={`${inputCls} w-20`} inputMode="decimal" placeholder="e.g. 0.5" value={hours} onChange={(e) => setHours(e.target.value)} />
        </label>
        {s.jobs.length > 1 ? (
          <label className={labelCls}>
            Billing job
            <select className={`${inputCls} w-56`} value={jobId} onChange={(e) => (setJobId(e.target.value), setTaskId(null))}>
              <option value="">— choose —</option>
              {s.jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className={labelCls}>
            Billing job
            <span className="py-1 text-[13.5px] text-eq-navy">{s.jobs[0]?.name}</span>
          </div>
        )}
        {tasks.length > 0 && (
          <label className={labelCls}>
            Task (optional)
            <select className={`${inputCls} w-36`} value={taskId ?? ''} onChange={(e) => setTaskId(e.target.value || null)}>
              <option value="">—</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className={`${labelCls} min-w-[16rem] flex-1`}>
          Narrative
          <input className={inputCls} value={narrative} maxLength={2000} onChange={(e) => setNarrative(e.target.value)} />
        </label>
        <button type="button" className={primaryBtn(busy || hours.trim() === '' || jobId === '')} disabled={busy || hours.trim() === '' || jobId === ''} onClick={() => void act('accept')}>
          Log it
        </button>
        <button type="button" className={quietBtn} disabled={busy} onClick={() => void act('dismiss')}>
          Dismiss
        </button>
      </div>
      {error && <p className="mb-0 mt-1 text-[12.5px] text-eq-danger">{error}</p>}
    </li>
  )
}

export function SuggestionsPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const signedIn = auth.mode === 'cognito' && auth.user !== null
  const today = todaySast()
  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="Suggestions"
      subtitle="Time you may want to log, from what you did on the due list in the last four weeks. Nothing is logged until you say so."
    >
      {!signedIn ? (
        <ErrorState message="Suggestions come from what you did, so they need sign-in. This server is running without Cognito (local development)." />
      ) : (
        <section className="rounded-eq-md border border-eq-border bg-white px-5 py-4">
          <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
            From your own work on obligation types linked to a billing job (Jobs and prices): moving a period to For review, completing it, or
            writing a note on it. Logging one makes a draft on <Link to="/time/my-week">My week</Link>; a dismissed one is not offered again.
            A day you already logged time for that client and job is left out.
          </p>
          <SuggestionList to={today} empty="No open suggestions in the last four weeks." />
        </section>
      )}
    </AppShell>
  )
}
